import { useState, useEffect } from "react";
import {
  backfillVoicePreviews,
  listAllReferences,
  listUsers,
  markReferenceProvided,
  resetStuckVoicePreviews,
} from "../../lib/api";
import type { Reference, UserProfile } from "../../lib/types";
import { Icon } from "../../components/icons";

type AdminRef = Reference & { userId: string; provided?: boolean };

type BackfillScope = "missing" | "stale" | "all";

export function Voices() {
  const [refs, setRefs] = useState<AdminRef[]>([]);
  const [users, setUsers] = useState<Map<string, UserProfile>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "provided" | "user">("all");
  const [search, setSearch] = useState("");
  const [toggling, setToggling] = useState<string | null>(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [refsResult, usersResult] = await Promise.all([
        listAllReferences(),
        listUsers(),
      ]);
      setRefs(refsResult.items as AdminRef[]);
      const userMap = new Map<string, UserProfile>();
      for (const u of usersResult.items) {
        userMap.set(u.userId, u);
      }
      setUsers(userMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleProvided(ref: AdminRef) {
    const key = `${ref.userId}:${ref.name}`;
    setToggling(key);
    setError(null);
    try {
      const newProvided = !ref.provided;
      await markReferenceProvided(ref.userId, ref.name, newProvided);
      setRefs((prev) =>
        prev.map((r) =>
          r.userId === ref.userId && r.name === ref.name
            ? { ...r, provided: newProvided }
            : r
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update");
    } finally {
      setToggling(null);
    }
  }

  const filtered = refs
    .filter((r) => {
      if (filter === "provided") return r.provided;
      if (filter === "user") return !r.provided;
      return true;
    })
    .filter((r) => {
      if (!search) return true;
      const q = search.toLowerCase();
      const ownerEmail = users.get(r.userId)?.email ?? "";
      return (
        r.name.toLowerCase().includes(q) ||
        ownerEmail.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      // Provided first, then by name
      if (a.provided && !b.provided) return -1;
      if (!a.provided && b.provided) return 1;
      return a.name.localeCompare(b.name);
    });

  const providedCount = refs.filter((r) => r.provided).length;
  const previewMissing = refs.filter(
    (r) => r.voicePreviewStatus !== "complete" || !r.voicePreviewS3Key,
  ).length;
  const previewFailed = refs.filter((r) => r.voicePreviewStatus === "failed").length;
  const previewStuck = refs.filter(
    (r) => r.voicePreviewStatus === "queued" || r.voicePreviewStatus === "processing",
  ).length;

  async function handleResetStuck() {
    if (resetting) return;
    setResetting(true);
    setBackfillResult(null);
    setError(null);
    try {
      const result = await resetStuckVoicePreviews();
      setBackfillResult(
        `Reset ${result.reset} stuck preview${result.reset === 1 ? "" : "s"} ` +
          `(scanned ${result.scanned} of ${result.total} total).`,
      );
      setTimeout(() => {
        loadData();
      }, 800);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reset failed");
    } finally {
      setResetting(false);
    }
  }

  async function handleBackfill(scope: BackfillScope) {
    if (backfilling) return;
    setBackfilling(true);
    setBackfillResult(null);
    setError(null);
    try {
      const result = await backfillVoicePreviews(scope);
      setBackfillResult(
        `Queued ${result.enqueued} preview${result.enqueued === 1 ? "" : "s"} ` +
          `(${result.skipped} skipped of ${result.total} total).`,
      );
      // Re-fetch refs after a beat so the status flips show up.
      setTimeout(() => {
        loadData();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  }

  return (
    <div className="content">
      <div className="ph">
        <div>
          <div className="ph-eyebrow">Admin · Voices</div>
          <h1 className="ph-title">Voice references</h1>
          <div className="ph-lede">
            {refs.length} total · {providedCount} marked provided ·{" "}
            {previewMissing} missing preview
            {previewFailed > 0 ? ` · ${previewFailed} failed` : ""}
            {previewStuck > 0 ? ` · ${previewStuck} stuck` : ""}. Toggle which
            references are available to all users.
          </div>
        </div>
        <div className="ph-actions">
          <button
            type="button"
            className={`btn btn-line${backfilling ? " disabled" : ""}`}
            onClick={() => handleBackfill("missing")}
            disabled={backfilling}
            title="Queue voice previews for any reference that doesn't have a completed preview"
          >
            <Icon.Spark />
            {backfilling ? "Queuing…" : `Backfill previews${previewMissing > 0 ? ` (${previewMissing})` : ""}`}
          </button>
          <button
            type="button"
            className={`btn btn-ghost${backfilling ? " disabled" : ""}`}
            onClick={() => handleBackfill("stale")}
            disabled={backfilling}
            title="Re-render previews for refs whose script version is stale"
          >
            <Icon.Refresh />
            Re-render stale
          </button>
          {previewStuck > 0 && (
            <button
              type="button"
              className={`btn btn-ghost${resetting ? " disabled" : ""}`}
              onClick={handleResetStuck}
              disabled={resetting}
              title="Flip refs stuck in queued/processing to failed (Modal timeout cleanup)"
            >
              <Icon.X />
              {resetting ? "Resetting…" : `Reset stuck (${previewStuck})`}
            </button>
          )}
        </div>
      </div>

      {backfillResult && <div className="info-banner">{backfillResult}</div>}
      {error && <div className="err-banner">{error}</div>}

      {/* Filters */}
      <div className="flex gap-2 items-center">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or owner email…"
          className="input flex-1"
        />
        <div className="flex gap-1">
          {(["all", "provided", "user"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${
                filter === f
                  ? "bg-indigo-500/20 text-indigo-400"
                  : "bg-slate-800 text-slate-400 hover:bg-slate-700"
              }`}
            >
              {f === "all" ? "All" : f === "provided" ? "🌐 Provided" : "👤 User"}
            </button>
          ))}
        </div>
      </div>

      {/* Reference list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card animate-pulse h-16" />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((ref) => {
            const owner = users.get(ref.userId);
            const key = `${ref.userId}:${ref.name}`;
            const isToggling = toggling === key;

            return (
              <div
                key={key}
                className={`card flex items-center gap-3 ${
                  ref.provided ? "border-emerald-500/20" : ""
                }`}
              >
                {/* Icon */}
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg shrink-0 ${
                    ref.provided
                      ? "bg-emerald-500/10"
                      : "bg-slate-800"
                  }`}
                >
                  {ref.provided ? "🌐" : "🎤"}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-200 truncate">
                    {ref.name}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-slate-500 truncate">
                      {owner?.email ?? ref.userId}
                    </span>
                    {ref.duration != null && (
                      <span className="text-xs text-slate-600">
                        {ref.duration}s
                      </span>
                    )}
                    {ref.provided && (
                      <span className="text-xs text-emerald-400">provided</span>
                    )}
                  </div>
                </div>

                {/* Toggle button */}
                <button
                  onClick={() => handleToggleProvided(ref)}
                  disabled={isToggling}
                  className={`text-xs px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap ${
                    ref.provided
                      ? "bg-red-500/10 text-red-400 hover:bg-red-500/20"
                      : "bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"
                  } ${isToggling ? "opacity-50" : ""}`}
                >
                  {isToggling
                    ? "…"
                    : ref.provided
                    ? "Remove Provided"
                    : "Mark Provided"}
                </button>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-8">
              {search ? "No matching voice references" : "No voice references found"}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
